<script setup lang="ts">
import Translator from "@/utils/common/Translator.ts";
import PrintIcon from "@/assets/editViewIcons/PrintIcon.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import PrintAsIcon from "@/assets/editViewIcons/PrintAsIcon.vue";
import ReviseIcon from "@/assets/editViewIcons/ReviseIcon.vue";
import UnReviseIcon from "@/assets/editViewIcons/UnReviseIcon.vue";
import FilterIcon from "@/assets/listViewIcons/FilterIcon.vue";
import TableColumnsIcon from "@/assets/listViewIcons/TableColumnsIcon.vue";
import TreeViewIcon from "@/assets/editViewIcons/TreeViewIcon.vue";
import RefreshIcon from "@/assets/editViewIcons/RefreshIcon.vue";
import ListViewToolbarUtils from "@/utils/common/ListViewToolbarUtils.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import OutputFormat from "@/utils/constants/OutputFormat.ts";
import BusinessFunction from "@/utils/constants/BusinessFunction.ts";
import AutoFitColIcon from "@/assets/listViewIcons/AutoFitColIcon.vue";
import {GridApi} from "ag-grid-community";
import AutoFitColToContentIcon from "@/assets/listViewIcons/AutoFitColToContentIcon.vue";
import {type Component, computed, ref} from "vue";
import MoreMenuItem from "@/utils/UIConstants/MoreMenuItem.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import MoreMenu from "@/components/inputs/MoreMenu.vue";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import AddRecordIcon from "@/assets/editViewIcons/AddRecordIcon.vue";
import DeleteIcon from "@/assets/editViewIcons/DeleteIcon.vue";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import CapabilityType from "@/utils/constants/CapabilityType.ts";
import FilterListIcon from "@/assets/listViewIcons/filterListIcon.vue";
import EditViewListPresenter from "@/utils/common/EditViewListPresenter.ts";
import AscSortIcon from "@/assets/listViewIcons/AscSortIcon.vue";
import {Dialog, Screen} from "quasar";
import ChangeSortFieldsPopup from "@/components/listView/ChangeSortFieldsPopup.vue";
import ListViewUtils from "@/utils/common/ListViewUtils.ts";
import ExportExcelIcon from "@/assets/listViewIcons/ExportExcelIcon.vue";
import DescendingSortIcon from "@/assets/widgetIcons/DescendingSortIcon.vue";
import ToolbarScreenAction from "@/components/EditView/widgets/ToolbarScreenAction.vue";
import {ToolbarAction} from "@/utils/presenters/interfaces.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import RemoveFilterIcon from "@/assets/editViewIcons/RemoveFilterIcon.vue";
import GUIActionPlaceholder = namaTypes.GUIActionPlaceholder;

const {isSideBarOpen, showToolbarLabels, isUltra} = storeToRefs(useNamaFlagsStore());
const props = defineProps<{ presenter: ListPresenter, supportsTimeline?: boolean }>();
const listScreenToolbarActions = computed<GUIActionPlaceholder[]>(() => {
  const actions: GUIActionPlaceholder[] = [];
  const presenter = props.presenter;
  if (presenter.layout?.listScreenToolbarActions?.length)
    actions.push(...presenter.layout?.listScreenToolbarActions)
  return actions;
})

const emit = defineEmits(['toggleFilterGroup', 'toggleTree', 'toggleSortDirection', "refreshTimelineData"]);
const isFilterEmpty = computed(() => !(props.presenter.criteriaTree.value && props.presenter.criteriaTree.value.length && props.presenter.criteriaTree.value[0].children && props.presenter.criteriaTree.value[0].children.length));
const isColumnChooserVisible = ref(false);
const showingPreventTooltip = ref(props.presenter.preventAutoLoad.value);
const showTimeline = defineModel<boolean>("showTimeline", {required: false, default: false})
const place = props.presenter?.fetchPlace();
const preferences = props.presenter.preferences;
const layout = props.presenter?.layout;
const gridApi = props.presenter.gridApi.value!;

const loadingStore = useLoadingStore();
const moreMenuItems = ref<MoreMenuItem[] | undefined>();
const moreMenu = ref<typeof MoreMenu | null>(null);

function isHideEntitySpecificFeatures(): boolean {
  if (layout && layout.hideEntitySpecificFeatures)
    return true
  return false;
}

function onRightClickOnSortDirIcon() {
  Dialog.create({
    component: ChangeSortFieldsPopup,
    componentProps: {
      presenter: props.presenter!,
    }
  }).onOk(() => {
    props.presenter!.gridApi.value?.refreshServerSide({purge: true});
  }).onCancel(() => {
    props.presenter!.gridApi.value?.refreshServerSide({purge: true});
  });
}

const listViewActions: ToolbarAction[] = [
  {
    component: AddRecordIcon,
    businessFunc: BusinessFunction.newRecord,
    checkIfEnabled: (a) => {
      return SecurityUtils.userCanCreate(props.presenter.entityType);
    },
    checkIfVisible: (a) => {
      if ((props.presenter! instanceof EditViewListPresenter))
        return false;
      return !isHideEntitySpecificFeatures();
    },
    onClick: async () => {
      await ListViewToolbarUtils.onNewRecord(place.query);
    }
  }, {
    component: RefreshIcon,
    businessFunc: BusinessFunction.refresh,
    onClick:
      async () => {
        if (showTimeline.value)
          return emit("refreshTimelineData")
        let api = (gridApi as GridApi);
        props.presenter.calledFromRefresh = true;
        props.presenter.preventAutoLoad.value = false;
        api.refreshServerSide();
      }
  }, {
    component: PrintIcon,
    businessFunc: BusinessFunction.print,
    checkIfVisible: () => {
      return !showTimeline.value
    },
    onClick: async () => {
      loadingStore.startLoading();
      await ListViewToolbarUtils.print(OutputFormat.PDF, AGGridUtils.fetchSelectedRows(gridApi), place, props.presenter?.layout?.id, gridApi)
      loadingStore.stopLoading();
    }
  }, {
    component: PrintAsIcon,
    businessFunc: BusinessFunction.printAs,
    checkIfVisible: () => {
      return !showTimeline.value
    },
    onClick: async () => {
      await ListViewToolbarUtils.onPrintAs(AGGridUtils.fetchSelectedRows(gridApi), place, props.presenter?.layout?.id, gridApi);
    }
  }, {
    component: ExportExcelIcon,
    businessFunc: BusinessFunction.exportAsXLSX,
    checkIfVisible: () => {
      return !showTimeline.value
    },
    onClick: async () => {
      loadingStore.startLoading();
      ListViewToolbarUtils.exportToExcel(place, gridApi)
      loadingStore.stopLoading();
    }
  }, {
    component: ReviseIcon,
    businessFunc: BusinessFunction.revise,
    checkIfEnabled: (a) => {
      return SecurityUtils.userCan(props.presenter.entityType, CapabilityType.Revise);
    },
    checkIfVisible: (a) => {
      if (props.presenter! instanceof EditViewListPresenter)
        return false;
      return !isHideEntitySpecificFeatures();
    },
    onClick: async () => {
      await ListViewToolbarUtils.markRevised(true, place, gridApi);
    }
  }, {
    component: UnReviseIcon,
    businessFunc: BusinessFunction.unrevise,
    checkIfEnabled: (a) => {
      return SecurityUtils.userCan(props.presenter.entityType, CapabilityType.Unrevise);
    },
    checkIfVisible: (a) => {
      if (props.presenter! instanceof EditViewListPresenter)
        return false;
      return !isHideEntitySpecificFeatures();
    },
    onClick: async () => {
      await ListViewToolbarUtils.markRevised(false, place, gridApi);
    }
  }, {
    component: AscSortIcon,
    businessFunc: BusinessFunction.toggleSortDirection,
    onClick: async () => {
      emit('toggleSortDirection');
    },
  }, {
    component: FilterIcon,
    businessFunc: BusinessFunction.filterAndSort,
    onClick: async () => {
      emit('toggleFilterGroup');
    }, checkIfVisible: (a) => {
      return isFilterEmpty.value;
    },
  }, {
    component: FilterListIcon,
    businessFunc: BusinessFunction.filterAndSort,
    onClick: async () => {
      emit('toggleFilterGroup');
    }, checkIfVisible: (a) => {
      return !isFilterEmpty.value;
    },
  }, {
    component: TableColumnsIcon,
    businessFunc: BusinessFunction.resultSettings,
    onClick: async () => {
      if (!isColumnChooserVisible.value)
        gridApi.showColumnChooser();
      else
        gridApi.hideColumnChooser();
      isColumnChooserVisible.value = !isColumnChooserVisible.value
    },
    checkIfVisible: (a) => {
      return !showTimeline.value;
    },
  }, {
    component: TreeViewIcon,
    businessFunc: BusinessFunction.treeView,
    checkIfEnabled: (a) => {
      if (layout && !isHideEntitySpecificFeatures() && layout.alwaysShowTreeViewButton)
        return true;
      if (ListViewToolbarUtils.supportsTreeViewInListView(props.presenter.entityType))
        return true
      return false;
    },
    checkIfVisible: (a) => {
      return ListViewToolbarUtils.supportsTreeViewInListView(props.presenter.entityType) && !showTimeline.value;
    },
    onClick: async () => {
      emit('toggleTree');
    }
  }, {
    component: AutoFitColIcon,
    businessFunc: BusinessFunction.autoFitCols,
    onClick: async () => {
      let api = (gridApi as GridApi);
      api.sizeColumnsToFit();
    },
    checkIfVisible: (a) => {
      return !showTimeline.value;
    }
  }, {
    component: AutoFitColToContentIcon,
    businessFunc: BusinessFunction.autoFitCols,
    onClick: async () => {
      let api = (gridApi as GridApi);
      api.autoSizeColumns(api.getColumns()!.map(l => l.getId()), false);
    },
    checkIfVisible: (a) => {
      return !showTimeline.value;
    }
  }, {
    component: DeleteIcon,
    businessFunc: BusinessFunction.$delete,
    checkIfVisible: (a) => {
      if (props.presenter! instanceof EditViewListPresenter)
        return false;
      return !isHideEntitySpecificFeatures();
    },
    onClick: async () => {
      ListViewToolbarUtils.deleteSelectedRows(props.presenter)
    }
  }, {
    component: RemoveFilterIcon,
    businessFunc: BusinessFunction.removeFilter,
    checkIfEnabled: (a) => ListViewToolbarUtils.isHasFilters(props.presenter),
    onClick: async () => {
      let api = (gridApi as GridApi);
      await ListViewToolbarUtils.removeFilter(props.presenter,api)
    }
  }
];

function onMoreMenuClick() {
  moreMenuItems.value = ListViewToolbarUtils.getListViewMoreMenuItems(props.presenter);
}

function onShowTimelineChange() {
  const tempValue = !showTimeline.value;
  showTimeline.value = tempValue;
  NamaCommon.setInStorage(IdsAndConstants.SHOW_TIMELINE, tempValue)
}

async function handleShortcut(functions: string[]): Promise<boolean> {
  let toolbarAction = listViewActions.find(a => functions.includes(a.businessFunc));
  if (toolbarAction && isActionEnabled(toolbarAction) && toolbarAction.onClick) {
    await toolbarAction.onClick();
    return true;
  }
  if (functions.includes(BusinessFunction.more)) {
    moreMenuItems.value = ListViewToolbarUtils.getListViewMoreMenuItems(props.presenter);
    moreMenu.value!.showPopup();
    return true;
  }
  let moreItems = ListViewToolbarUtils.getListViewMoreMenuItems(props.presenter);
  let item = moreItems.find(i => i.id && functions.includes(i.id));
  if (item) {
    await item.callGUIAction(item, preferences.metaData!);
    return true;
  }
  if (functions.includes(FIRST)) {
    gridApi.paginationGoToFirstPage();
    return true;
  } else if (functions.includes(LAST)) {
    gridApi.paginationGoToLastPage();
    return true;
  } else if (functions.includes(NEXT)) {
    gridApi.paginationGoToNextPage();
    return true;
  } else if (functions.includes(PREVIOUS)) {
    gridApi.paginationGoToPreviousPage();
    return true;
  }
  return false;
}

function isActionVisible(action: ToolbarAction) {
  if (!action.checkIfVisible)
    return true;
  return action.checkIfVisible(action);
}

function isActionDisabled(action: ToolbarAction) {
  return !isActionEnabled(action);
}

function isActionEnabled(action: ToolbarAction) {
  if (!action.checkIfEnabled)
    return true;
  return action.checkIfEnabled(action);
}

const isFilterSortActionActive = computed(() => (action: ToolbarAction) => {
  if (action.businessFunc != BusinessFunction.filterAndSort)
    return false;
  return !isFilterEmpty.value;
})

const FIRST = "first";
const LAST = "last";
const NEXT = "next";
const PREVIOUS = "previous";
const isASCSortDir = computed(() => props.presenter?.sortDirectionValue.value.value === ListViewUtils.ascSortDir().value);
const renderActionComponent = computed<(a: ToolbarAction) => Component>(() => (action) => {
  if (action.businessFunc == BusinessFunction.toggleSortDirection && !isASCSortDir.value)
    return DescendingSortIcon;
  return action.component;
})
defineExpose({handleShortcut});
</script>

<template>
  <div class="tw-flex tw-gap-2 tw-items-center" :class="[(isUltra && Screen.lt.sm) || !isUltra ? 'tw-justify-between' : 'tw-justify-end']">
    <div class="tw-flex tw-gap-2 tw-flex-nowrap tw-overflow-x-auto"
         :class="{'laptop:tw-flex-nowrap laptop:tw-overflow-x-auto':isSideBarOpen}">
      <template v-for="action in listViewActions" :key="action.businessFunc">
        <template v-if="isActionVisible(action)">
          <a
              class="tw-justify-center hover:tw-bg-gray-200 tw-flex tw-flex-col tw-gap-1 tw-items-center active:tw-bg-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-2 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer"
            :class="{'nama-toolbar-disabled':isActionDisabled(action),'tw-bg-gray-200 dark:tw-bg-gray-700':isFilterSortActionActive(action)}"
            @click.stop="action.onClick" :title="Translator.translateId(action.businessFunc)"
            @contextmenu.prevent="(e)=>BusinessFunction.toggleSortDirection===action.businessFunc?onRightClickOnSortDirIcon():undefined">
            <component
              :is="renderActionComponent(action)"
              class="tw-h-[22px]  ultra:tw-size-5" :class="{'!tw-fill-gray-300 dark:!tw-fill-gray-600':isActionDisabled(action)}"/>
            <span
                v-if="showToolbarLabels && !isUltra"
                v-text="Translator.translateWithFallback(Translator.translateId(action.businessFunc,'BusinessFunction'),action.businessFunc+'Short')"
                class="tw-select-none tw-whitespace-nowrap tw-text-xs tw-text-namaFontColor-darkGray dark:tw-text-namaFontColor-dark tw-capitalize"/>
            <q-tooltip v-if="action.businessFunc === BusinessFunction.filterAndSort && presenter.preventAutoLoad.value"
                       v-model="showingPreventTooltip"
                       v-text="Translator.translateNaMaText(AGGridUtils.autoLoadDisabledMsg)"
                       anchor="bottom middle" self="center middle" class="tw-bg-red-700 tw-text-sm tw-max-w-fit"/>
          </a>
        </template>
      </template>
      <a v-if="supportsTimeline" @click.stop="onShowTimelineChange"
         :class="{'dark:tw-bg-gray-600 tw-bg-gray-300':showTimeline}"
         class="tw-inline-flex tw-flex-col tw-items-center tw-justify-center dark:hover:tw-bg-gray-700 hover:tw-bg-gray-300 dark:active:tw-bg-gray-600 tw-py-1.5 tw-px-2 tw-rounded-lg tw-cursor-pointer tw-gap-1">
        <NamaIcon class="tw-size-[22px]" default-icon="nm-timeline"/>
        <span v-if="showToolbarLabels" class="tw-text-xs tw-select-none tw-whitespace-nowrap tw-text-namaFontColor-light/70 dark:tw-text-white/70"
              v-text="Translator.translateId('Timeline')"/>
      </a>
      <template v-for="action in listScreenToolbarActions">
        <ToolbarScreenAction :action="action" :presenter="presenter"/>
      </template>
    </div>
    <span class="tw-flex tw-items-center tw-gap-2 tw-justify-center">
      <a
          class="tw-justify-center tw-flex tw-flex-col tw-gap-1 tw-items-center hover:tw-bg-gray-200 active:tw-bg-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-2 ultra:tw-p-1 ultra:tw-my-1 tw-rounded-lg tw-cursor-pointer"
        @click.stop="onMoreMenuClick" :title="Translator.translateId(BusinessFunction.more)">
        <MoreMenu :metadata="preferences.metaData!" :moreMenuItems="moreMenuItems" ref="moreMenu"/>
        <span
            v-if="showToolbarLabels && !isUltra" v-text="Translator.translateId('more')"
            class="tw-text-namaFontColor-darkGray dark:tw-text-namaFontColor-dark tw-text-xs tw-select-none"/>
      </a>
    </span>
  </div>
</template>

<style>
.tw-dark #listview-more-menu {
  scrollbar-color: #8b8b8b #2c2c2c;
}
</style>
